// frontend/src/pages/PaychequeDetail/helpers.ts
// Pure helpers for PaychequeDetail: auth, document title building, print-mode detection.
// No React, no state.

import type { PaychequeDetail } from "./types";

/**
 * Read the auth token from localStorage. Checks both current and legacy keys.
 */
export function getToken(): string {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

/**
 * Bearer-token + JSON content-type headers for API requests with a body.
 */
export function authHeaders(): Record<string, string> {
  return {
    Authorization: "Bearer " + getToken(),
    "Content-Type": "application/json",
  };
}

/**
 * Document title set when the paycheque data loads (used for save-as-PDF filename).
 * Format: paystub_{SafeName}_{YYYY-MM-DD}
 */
export function buildDocTitle(pc: PaychequeDetail): string {
  const safeName = (pc.employee_name || "employee").replace(/[^A-Za-z0-9_-]/g, "_");
  const dateStr = pc.pay_date || pc.pay_period_end || "";
  return "paystub_" + safeName + "_" + dateStr;
}

/**
 * Document title set right before the browser print dialog opens.
 * Human-readable format: "Pay stub - Name - dd-mm-yyyy"
 */
export function buildPrintTitle(pc: PaychequeDetail): string {
  const name = (pc.employee_name || "Employee").replace(/[^a-zA-Z0-9 -]/g, "");
  let dateStr = "";
  if (pc.pay_date) {
    const d = new Date(pc.pay_date);
    if (!isNaN(d.getTime())) {
      const dd = String(d.getUTCDate()).padStart(2, "0");
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      dateStr = dd + "-" + mm + "-" + d.getUTCFullYear();
    }
  }
  return "Pay stub - " + name + (dateStr ? " - " + dateStr : "");
}

/**
 * True when the page URL has ?print=1 (used by the paycheque list kebab menu
 * to open the detail page and auto-fire the print dialog).
 */
export function isPrintMode(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("print") === "1";
}